/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Thursday, 5th September 2019
 *
 * Mobx Company Store
 */
import { configure, observable, action, decorate, reaction } from 'mobx';
import { firebase } from 'containers/services/Firebase';
configure({ enforceActions: 'observed' });

class VendorStore {
  badgeHistory = observable([]);
  isBadgeHistoryLoading = true;

  constructor(rootStore) {
    this._stores = rootStore;

    /**
     * Waits until user is authenticated, then initializes
     * the store.  Once initialized, it will dispose itself.
     */
    this._stores.appStore.debug(`VendorStore [listener] Waiting to init vendor store...`);
    reaction(
      () => this._stores.authStore.isAuth && this._stores.authStore.isVendorUser,
      isVendor => {
        this._stores.appStore.debug(`VendorStore [reaction] Detected a change in user auth status.`);
        if (isVendor) {
          this.initStore();
        } else {
          this._stores.appStore.debug(`VendorStore [reaction] Detected a user signout event, resetting store.`);
          this.reset();
        }
      }
    );

    // Bind methods to (this) if used inside a promise
    this.setField = this.setField.bind(this);
    this.setArrayField = this.setArrayField.bind(this);
  }

  /**
   * Internal stores getter.  Note: NOT mobx computed
   */
  get stores() {
    return this._stores;
  }

  /**
   * Generic action to set value of any store field.
   * @param {string} fieldName Name of store field to set.
   * @param {any} value Value of store field to set.
   * @returns {void}
   */
  setField(fieldName, value) {
    if (this[fieldName] !== undefined) {
      this[fieldName] = value;
    }
  }

  /**
   * Generic action to replace store array index value.
   * @param {array} array Store array to update
   * @param {int} index Array index to replace.
   * @param {any} newValue New array value.
   * @returns {void}
   */
  setArrayField(array, index, newValue) {
    array[index] = newValue;
  }

  /**
   * Initializes vendor store
   * Mobx: action
   * @returns {void}
   */
  initStore = () => {
    this.fetchBadgeHistory();
  };

  /**
   * Fetches the badge history for entire company.
   * @function fetchBadgeHistory
   * @returns {void}
   */
  fetchBadgeHistory = async () => {
    this.setField('isBadgeHistoryLoading', true);
    this.stores.appStore.debug('VendorStore.fetchBadgeHistory() [action] Fetching badge history...');
    firebase
      .getVendorCheckins(this.stores.authStore.currentUser.uid)
      .then(badgeHistory => {
        this.setField('badgeHistory', badgeHistory);
        this.setField('isBadgeHistoryLoading', false);
        this.stores.appStore.debug('VendorStore.fetchBadgeHistory() [action] Badge history retrieved.', badgeHistory);
      })
      .catch(error => {
        this.setField('isBadgeHistoryLoading', false);
        this.stores.appStore.error('system', error);
      });
  };

  /**
   * Resets store values to default
   */
  reset = () => {
    this.badgeHistory = observable([]);
    this.isBadgeHistoryLoading = true;
    this.stores.appStore.debug('VendorStore.reset() [action] Vendor store has been reset.');
  };
}
decorate(VendorStore, {
  isBadgeHistoryLoading: observable,
  setField: action,
  setArrayField: action,
  initStore: action,
  reset: action,
});

export default VendorStore;
